// frontend/src/components/article/ArticleContent.js
import React, { useEffect, useRef, useState } from "react";
import VideoModal from "../common/VideoModal";

const ArticleContent = ({ article }) => {
  const contentRef = useRef(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  // Clean the title
  const cleanTitle = article.title?.replace(/<br\s*\/?>/gi, ' ').trim();

  useEffect(() => {
    // Scroll to top when article loads
    window.scrollTo(0, 0);

    // Create global burnairMap object for video playback
    if (!window.burnairMap) {
      window.burnairMap = {};
    }

    window.burnairMap.playVideo = (videoUrl) => {
      console.log('Playing video:', videoUrl);
      setCurrentVideoUrl(videoUrl);
      setVideoModalOpen(true);
    };

    // Cleanup function
    return () => {
      if (window.burnairMap && window.burnairMap.playVideo) {
        delete window.burnairMap.playVideo;
      }
    };
  }, []);

  useEffect(() => {

    // Apply styles to content after render
    if (contentRef.current) {
      // Style all h1 elements
      const h1Elements = contentRef.current.querySelectorAll('h1');
      h1Elements.forEach(h1 => {
        h1.style.fontSize = '2.25rem';
        h1.style.fontWeight = 'bold';
        h1.style.color = '#002855';
        h1.style.marginTop = '2.5rem';
        h1.style.marginBottom = '0.5rem';  // Reduced from 1.5rem to 0.5rem (1/3)
        h1.style.paddingBottom = '0.75rem';
        h1.style.borderBottom = '2px solid #e5e7eb';
        h1.style.lineHeight = '1.2';  // Tighter line spacing
      });

      // Style all h2 elements
      const h2Elements = contentRef.current.querySelectorAll('h2');
      h2Elements.forEach(h2 => {
        h2.style.fontSize = '1.875rem';
        h2.style.fontWeight = 'bold';
        h2.style.color = '#84BD00';
        h2.style.marginTop = '2rem';
        h2.style.marginBottom = '0.33rem';  // Reduced from 1rem to 0.33rem (1/3)
        h2.style.lineHeight = '1.25';  // Tighter line spacing
      });

      // Style all h3 elements
      const h3Elements = contentRef.current.querySelectorAll('h3');
      h3Elements.forEach(h3 => {
        h3.style.fontSize = '1.5rem';
        h3.style.fontWeight = '600';
        h3.style.color = '#84BD00';
        h3.style.marginTop = '1.75rem';
        h3.style.marginBottom = '0.25rem';  // Reduced from 0.75rem to 0.25rem (1/3)
        h3.style.lineHeight = '1.3';  // Tighter line spacing
      });

      // Style paragraphs
      const pElements = contentRef.current.querySelectorAll('p');
      pElements.forEach(p => {
        p.style.marginBottom = '1.25rem';
        p.style.color = '#374151';
        p.style.lineHeight = '1.75';
      });

      // Style lists
      const ulElements = contentRef.current.querySelectorAll('ul');
      ulElements.forEach(ul => {
        ul.style.marginBottom = '1.25rem';
        ul.style.paddingLeft = '1.5rem';
      });

      const olElements = contentRef.current.querySelectorAll('ol');
      olElements.forEach(ol => {
        ol.style.marginBottom = '1.25rem';
        ol.style.paddingLeft = '1.5rem';
      });

      const liElements = contentRef.current.querySelectorAll('li');
      liElements.forEach(li => {
        li.style.marginBottom = '0.5rem';
        li.style.lineHeight = '1.75';
        li.style.color = '#374151';
        if (!li.hasAttribute('data-icon')) {
          if (li.parentElement.tagName === 'UL') {
            li.style.listStyleType = 'disc';
          } else if (li.parentElement.tagName === 'OL') {
            li.style.listStyleType = 'decimal';
          }
        }
      });

      // Style links
      const aElements = contentRef.current.querySelectorAll('a');
      aElements.forEach(a => {
        a.style.color = '#00A3E0';
        a.style.textDecoration = 'underline';
        a.style.transition = 'color 0.2s';
        a.onmouseover = () => { a.style.color = '#002855'; };
        a.onmouseout = () => { a.style.color = '#00A3E0'; };
      });

      // Style and fix image URLs
      const imgElements = contentRef.current.querySelectorAll('img');
      imgElements.forEach(img => {
        let src = img.getAttribute('src');

        if (src) {
          src = src.trim();

          // Fix double URL issue
          const baseUrl = 'https://www.burnair.cloud/images/news/';

          // Count occurrences of the base URL
          const occurrences = (src.match(/https:\/\/www\.burnair\.cloud\/images\/news\//g) || []).length;

          if (occurrences > 1) {
            // Find the last occurrence and use everything from there
            const lastIndex = src.lastIndexOf(baseUrl);
            src = src.substring(lastIndex);
          }

          // URL encode spaces
          src = src.replace(/ /g, '%20');
          img.setAttribute('src', src);
        }

        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.borderRadius = '0.5rem';
        img.style.margin = '1.5rem 0';
      });

      // Style blockquotes
      const blockquoteElements = contentRef.current.querySelectorAll('blockquote');
      blockquoteElements.forEach(blockquote => {
        blockquote.style.borderLeft = '4px solid #00A3E0';
        blockquote.style.paddingLeft = '1.5rem';
        blockquote.style.margin = '1.5rem 0';
        blockquote.style.fontStyle = 'italic';
        blockquote.style.color = '#6b7280';
      });

      // Style strong/bold
      const strongElements = contentRef.current.querySelectorAll('strong, b');
      strongElements.forEach(strong => {
        strong.style.fontWeight = '600';
        strong.style.color = '#1f2937';
      });

      // Style hr
      const hrElements = contentRef.current.querySelectorAll('hr');
      hrElements.forEach(hr => {
        hr.style.border = 'none';
        hr.style.borderTop = '2px solid #e5e7eb';
        hr.style.margin = '2rem 0';
      });

      // Style tables
      const tableElements = contentRef.current.querySelectorAll('table');
      tableElements.forEach(table => {
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.margin = '1.5rem 0';
      });

      const thElements = contentRef.current.querySelectorAll('th');
      thElements.forEach(th => {
        th.style.padding = '0.75rem';
        th.style.border = '1px solid #e5e7eb';
        th.style.textAlign = 'left';
        th.style.background = '#f9fafb';
        th.style.fontWeight = '600';
        th.style.color = '#374151';
      });

      const tdElements = contentRef.current.querySelectorAll('td');
      tdElements.forEach(td => {
        td.style.padding = '0.75rem';
        td.style.border = '1px solid #e5e7eb';
        td.style.textAlign = 'left';
      });

      // Style lists with data-icon
      const iconListItems = contentRef.current.querySelectorAll('li[data-icon]');
      iconListItems.forEach(li => {
        li.style.listStyle = 'none';
        li.style.position = 'relative';
        const icon = li.getAttribute('data-icon');

        // Check if icon hasn't been added already (avoid duplicates)
        if (!li.dataset.iconProcessed) {
          // Get the current text content without any existing icon spans
          const textContent = li.textContent.replace(new RegExp(icon, 'g'), '').trim();
          li.innerHTML = `<span style="margin-right: 0.5rem;">${icon}</span>${textContent}`;
          li.dataset.iconProcessed = 'true';
        }
      });
    }
  }, [article.content]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header with image and title overlay - if headerImage exists */}
      {article.headerImage && (
        <div className="mb-8 rounded-lg overflow-hidden shadow-md">
          <div className="relative aspect-video">
            <img
              src={(() => {
                let url = article.headerImage.trim();

                // Fix if URL got doubled up
                if (url.includes('https://www.burnair.cloud/images/news/') &&
                    url.includes('https://www.burnair.cloud/images/news/', 10)) {
                  // Find the second occurrence and use that
                  const secondIndex = url.indexOf('https://www.burnair.cloud/images/news/', 10);
                  url = url.substring(secondIndex);
                }

                // Only add base URL if it's not already a full URL
                if (!url.startsWith('http')) {
                  url = `https://www.burnair.cloud/images/news/${url}`;
                }

                // URL encode spaces
                return url.replace(/ /g, '%20');
              })()}
              alt={cleanTitle}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                {cleanTitle}
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* If no header image, show title as regular heading */}
      {!article.headerImage && (
        <h1 className="text-3xl md:text-4xl font-bold text-[#002855] mb-6">
          {cleanTitle}
        </h1>
      )}

      {/* Article content with clean styling */}
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8 border border-gray-200">
        <div
          ref={contentRef}
          className="prose prose-lg max-w-none"
          style={{ color: '#374151', fontSize: '1.125rem', lineHeight: '1.75' }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setCurrentVideoUrl("");
        }}
        videoUrl={currentVideoUrl}
        title={cleanTitle}
      />
    </div>
  );
};

export default ArticleContent;